/**
 * client/src/cma/CompNumInput.tsx — one editable figure in the comparable set.
 *
 * LIFTED OUT OF `ConfirmGrid` (2026-08-29) so the card surface and the table can share it rather
 * than growing two spellings of the same control. Nothing about its behaviour changed in the move:
 * the same wheel guard, the same `needed` ring, the same pencil mark and the same two sentences
 * behind it.
 */
import { themeVar as colors } from "../design-tokens";
import { money } from "./compFigures";

/**
 * HOW THE FIGURE READS WHEN NOBODY IS EDITING IT.
 *
 * `undefined` — the digits exactly as stored, which is right for a year (2003, never 2,003), a
 * bedroom count or a bath count.
 * `"money"` — this module's own `compFigures.money`, the same function the comparable CARDS
 * already print a sold price with. It is the house rendering of a comp figure and is not a second
 * opinion invented here.
 * `"grouped"` — thousands separators and no currency mark, for a square footage.
 *
 * IT NEVER APPLIES TO A `MoneyString`. `CandidateComp.price` and `.seller_concession` are
 * `number | null` on the wire (`cma/types.ts`) — a CMA comp figure is a number this client already
 * does arithmetic on, and `compFigures.money` is the existing, sanctioned formatter for it. The
 * repo-wide "render verbatim, never parse" rule is about `crm/types.ts`'s `MoneyString` and
 * `editor/figures.ts`'s branded string, which are SERVER-FORMATTED TEXT and are not touched here.
 */
export type FigureFormat = "money" | "grouped";

/** The at-rest rendering of a figure, or `null` when there is nothing to render.
 *
 * ROUNDING IS REFUSED, NOT ABSORBED. `money()` rounds to whole dollars, which is correct for the
 * cards (they print a figure) and wrong for a field (it would show a figure the input does not
 * hold, and the agent would be proof-reading a number they cannot edit). So a non-integer value
 * declines the overlay entirely and shows its own digits. A displayed figure and the value behind
 * it are the same number here, always. */
export function figureDisplay(value: number, format?: FigureFormat): string | null {
  if (!format || !Number.isFinite(value) || !Number.isInteger(value)) return null;
  return format === "money" ? money(value) : value.toLocaleString("en-US");
}

export function CompNumInput({
  value,
  edited,
  original,
  onChange,
  prefix,
  format,
  width = 90,
  needed = false,
}: {
  value: number | null;
  edited: boolean;
  original?: number;
  onChange: (v: number | null) => void;
  prefix?: string;
  /** See `FigureFormat`. Absent means the digits render exactly as stored. */
  format?: FigureFormat;
  width?: number;
  /** This row cannot be selected until this particular cell has a value. Draws the cell as the
   * thing to fix, so the agent's eye lands on the input rather than on a row-level sentence they
   * then have to map back onto ten columns. */
  needed?: boolean;
}) {
  const blank = value == null || (needed && !edited && value <= 0);
  const display = blank ? null : figureDisplay(value as number, format);
  return (
    // THE CELL IS A FIGURE FIRST AND A CONTROL SECOND (2026-08-29). `width` is kept in the API and
    // is now a FLOOR rather than a fixed size: the field fills its column and right-aligns, so every
    // number in a column shares one edge and the eye can compare down it. The box itself resolves on
    // hover and on focus — see `.vb-cell` in app.css for why that is CSS and not an inline style.
    <span className="vb-cellwrap" data-fmt={display ? "on" : undefined}>
      {/* THE FIELD AND ITS OVERLAY ARE ONE BOX; the pencil is beside it, not inside it. The mark
          used to be an inline sibling of a `width: 100%` input inside a `display: block` wrapper,
          so it wrapped onto a second line and made an edited row taller than its neighbours. */}
      <span className="vb-cellfield">
      {/* THE CURRENCY MARK IS INSIDE THE FIGURE, NOT BESIDE IT  (2026-08-31, owner, from a
          screenshot of the Sold price column: "the sold price looks out of alignment ... make sure
          it doesn't get cut off as it is now").

          WHAT WAS ACTUALLY WRONG, because the two symptoms had one cause. The `$` was a SIBLING of
          the input in a `justify-content: flex-end` row, and the input was `flex: none` at a fixed
          90px inside a 132px column. So the pair was pushed to the right edge as a unit and the
          leftover ~16px opened up between the cell's left edge and the `$` — the mark floated in
          the middle of the cell, attached to nothing. And because the input was fixed at 90px
          regardless of the column it sat in, a nine-character figure had nowhere to go: a
          `type="number"` input does not ellipsise, it simply clips, which is how `294708.93` came
          to render as `294708.9`. Widening the column could not fix it; the input was not listening
          to the column.

          Both go away by making the prefix part of the input's own box. The mark is absolutely
          positioned inside the input's left padding, so it cannot drift from the digits; the input
          fills the column (`width: 100%`) with the `width` prop kept as a MINIMUM, so a wide column
          gives the figure room and a narrow one still gets a usable field. The digits stay right-
          aligned on the column's right edge with tabular numerals, which is what the design doc
          asks for and is why these are not centred: a column of numbers is read down its last
          digit, and centring destroys the only edge that comparison uses. */}
      {/* THE MARK ONLY EXISTS WHEN THE OVERLAY DOES NOT. See `.vb-cellfmt` in app.css for the
          defect: an absolutely-positioned `$` inside a field that fills its column ends up at the
          COLUMN's left edge while its digits are right-aligned at the column's right edge, which
          in a 132px Sold price column put the two ~120px apart. When a formatted overlay is
          rendering, the mark is part of that one string and cannot drift from it. */}
        {prefix && !display && <span className="vb-cellpre" aria-hidden="true">{prefix}</span>}
        <input
        type="number"
        className={prefix ? "vb-cell vb-cell-pre" : "vb-cell"}
        data-needed={needed || undefined}
        data-edited={edited || undefined}
        /* A FIGURE THE APP HAS ALREADY CLASSIFIED AS ABSENT IS NOT SHOWN AS A NUMBER
           (2026-08-29, owner, from a live Denver run).

           The screenshot that prompted this had a row saying "Needs lot size and garage bays — the
           public record doesn't carry them" while the Lot (SF) box beside it displayed `0`. Both
           came from the same field: the provider returned 0, `compMissingFields` correctly read
           that as absent (a lot cannot be 0 SF), and the input rendered it anyway. So the screen
           asserted two contradictory things about one number, and the more visible of the two was
           the false one — a zero, in a box, that an agent can tab straight past.

           Unknown is never zero. When the app is treating this cell as empty, it renders empty,
           with the `needed` placeholder that says so.

           `!edited` is the whole subtlety. Blanking on `needed` alone would make a human-typed `0`
           disappear as they typed it — the value round-trips through the parent, comes back still
           non-positive, and the character vanishes. So only the AS-PULLED zero is suppressed; once
           a person has touched the field, whatever they typed is shown back to them, and the row
           stays blocked by the ring and the note rather than by silently eating the keystroke. */
        value={value == null || (needed && !edited && value <= 0) ? "" : value}
        placeholder={needed ? "needed" : "—"}
        aria-invalid={needed || undefined}
        title={needed ? "Required before this sale can be included" : undefined}
        onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))}
        // UI-064: a number input captures the mouse wheel — scrolling over it silently changed the
        // value AND ate the scroll gesture. Blur on wheel so the page scrolls and the value is safe.
        onWheel={(e) => e.currentTarget.blur()}
        // `width` IS A FLOOR, NOT A SIZE. The field fills its column and right-aligns, so every
        // number in a column shares one edge and the eye can compare straight down it; the prop
        // survives as the minimum a cell may shrink to, which is what keeps a narrow column from
        // collapsing the field to nothing. (It was `width: <n>px; flex: none`, and that fixed size
        // is what clipped a nine-character sold price inside a 132px column.)
        style={{ minWidth: width }}
      />
      {/* THE FIGURE AS A READER READS IT, OVER THE FIELD THAT EDITS IT  (2026-09-01, W2-UIFIX,
          from `w1-harness/step3-comps@1920.png`: the Sold price column printed `612000`).

          WHY AN OVERLAY AND NOT A FORMATTED INPUT. `<input type="number">` will not hold
          `612,000` — a grouped string is not a valid floating-point literal, so the browser
          reports the field empty and the value is lost on the next keystroke. Every product that
          shows `$612,000` in an editable money field is showing two things: a formatted READING
          and, underneath, the field. That is what this is.

          NOTHING IS HIDDEN AND NOTHING IS CHANGED. The overlay is `aria-hidden`, so assistive
          technology reads the input's own value and never a second copy of it. It is painted only
          while the field is at rest — hover or focus and it steps aside, revealing the exact
          digits the input holds, which is the only string the agent can type against. And
          `figureDisplay` refuses to render at all unless the formatted figure is the SAME NUMBER
          as the value: no rounding, no truncation, no unit conversion between what is read and
          what is stored. */}
      {display && (
          <span className="vb-cellfmt" aria-hidden="true">{display}</span>
        )}
      </span>
      {edited && (
        // TWO DIFFERENT FACTS, TWO DIFFERENT SENTENCES (2026-08-04). "Corrected by you" is only
        // true when the file actually stated something to correct. On a field the file left blank
        // — now a routine case, since those rows are no longer deleted — the honest statement is
        // that the agent supplied a figure the source never carried. Both still render the same
        // mark, because both mean "this number did not come from the source as printed".
        <span
          className="vb-cellmark"
          title={original == null
            ? "Not stated in your file — you supplied this"
            : `As pulled: ${original} — corrected by you`}
          style={{ color: colors.caution }}
        >
          ✎
        </span>
      )}
    </span>
  );
}
